'use strict';
import { confirmAiAction, createThread } from '@api/aiAnalyzer.js';
import { getQuotation } from '@api/quotation.js';
import { editUserDetails } from '@api/user';
import { AiAnalyzerBookmarks } from '@components/AiAnalyzer/AiAnalyzerBookmark.js';
import { AiAnalyzerChatTabs } from '@components/AiAnalyzer/AiAnalyzerChatTabs.js';
import { AiAnalyzerConfirmation } from '@components/AiAnalyzer/AiAnalyzerConfirmation.js';
import { AiAnalyzerEmptyState } from '@components/AiAnalyzer/AiAnalyzerEmptyState.js';
import { AiAnalyzerHistory } from '@components/AiAnalyzer/AiAnalyzerHistory.js';
import { AiAnalyzerReportSection } from '@components/AiAnalyzer/AiAnalyzerReportSection.js';
import { AiAnalyzerResult } from '@components/AiAnalyzer/AiAnalyzerResult.js';
import UploadMessagesView from '@components/Chat/Views/UploadMessagesView.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Tooltip from '@components/Common/Tooltip.js';
import ProjectTypePicker from '@components/Projects/header/ProjectTypePicker.js';
import { IS_MOBILE } from '@constants/global.js';
import { ArrowLeftIcon, ChevronIcon, CoinsIcon } from '@icons/index.js';
import UIIcon from '@icons/UIIcon.js';
import VisionHistoryPanel from '@pages/SpatialVision/components/VisionHistoryPanel.js';
import { createAutomatedTagsForExistingImage } from '@pages/SpatialVision/utils/automatedTagsExport.js';
import {
  isVlmImageOversized,
  partitionVlmImages,
} from '@pages/SpatialVision/utils/imageUploadLimits.js';
import { route } from '@router/index.js';
import {
  aiSelectionActionChanged,
  aiSelectionModeChanged,
  aiViewChange,
} from '@signals/aiAnalyzer/index.js';
import { projectListUpdated } from '@signals/CommonSignals.js';
import { deleteTag } from '@signals/viewer';
import {
  AiSelectedContentState,
  IsAiSelectionModeState,
} from '@states/AiAnalyzer.js';
import FoldersState from '@states/FoldersState.js';
import OrganizationState from '@states/Organization';
import ProjectsState, { AiSelectedItems } from '@states/Projects.js';
import { SpatialVisionState } from '@states/SpatialVision.js';
import UserState from '@states/User.js';
import { AvailableModelState, CadFileState, MemberState } from '@states/Viewer';
import { UIButton, UIDiv, UIPanel, UIParagraph, UIText } from '@ui';
import {
  getCadFileModelId,
  getReconstructionId,
  isViewerFileSource,
} from '@utils/cadFileModel.js';
import { CaseInsensitiveURLSearchParams } from '@utils/caseInsensitiveURLSearchParams';
import { getVisibleColumns } from '@utils/columnUtils.js';
import { handleDownloadPdf } from '@utils/html2pdf.js';
import _socket from '@utils/socket';
import {
  getCurrentOrganizationId,
  getCurrentSpaceCredits,
  updateCurrentSpaceCredits,
} from '@utils/userUtils';
import UniversalCookie from 'universal-cookie';
import { APIURL, S3URL } from '/config.js';

const AUTOMATED_TAG_JOB_POLL_INTERVAL_MS = 2000;
const AUTOMATED_TAG_JOB_MAX_POLLS = 120;

const debugAutomatedTagSource = source => ({
  source: source?.source || null,
  taskId: source?.taskId || null,
  cadFileModelId: getCadFileModelId(source),
  reconstructionId: getReconstructionId(source),
  hasPath: Boolean(source?.path || source?.filePath),
  hasFile: Boolean(source?.file),
  originalName: source?.originalName || source?.name || null,
});
const AUTOMATED_TAG_JOB_TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'canceled',
  'partial',
]);

class DrawerAiAnalyser extends UIPanel {
  constructor(isPage = false) {
    super();
    this.setId('drawer-ai-analyser');
    this.setClass('hidden h-full w-full border-0 px-2 pt-0 pb-1');

    // Our internal state
    this.chatSessions = new Map(); // Store multiple chat sessions
    this.currentSessionId = null;
    this.messagesContainer = null;
    this.welcomeScreen = null;

    this.creditsCount = new UIText(`${getCurrentSpaceCredits() || 0}`);

    this.isPage = isPage; // Flag to determine if this is a page or a drawer

    // Selection mode state
    this.isSelectionMode = false;
    this.normalHeader = null;
    this.selectionHeader = null;
    this.spatialVisionHistoryOpen = false;

    UserState.subscribe(this.#updateCreditsLabel);
    OrganizationState.subscribe(this.#updateCreditsLabel);

    // Subscribe to selection mode changes
    this.selectionModeListener = isActive => {
      this.isSelectionMode = isActive;
      this.#toggleSelectionModeUI(isActive);
    };
    aiSelectionModeChanged.add(this.selectionModeListener);

    // Subscribe to selection action changes
    this.selectionActionListener = action => {
      this.#handleSelectionAction(action);
    };
    aiSelectionActionChanged.add(this.selectionActionListener);

    // Subscribe to AI view changes for upload view
    this.aiViewChangeListener = (view, data) => {
      this.#handleAiViewChange(view, data);
    };
    aiViewChange.add(this.aiViewChangeListener);

    // Subscribe to selected content state for updating UI
    this.unsubscribeSelectedContent = AiSelectedContentState.subscribe(() => {
      this.#updateSelectionCount();
    });
    this.unsubscribeSpatialVisionHistory = SpatialVisionState.subscribe(
      state => {
        if (this.spatialVisionHistoryPanel && this.spatialVisionHistoryOpen) {
          this.spatialVisionHistoryPanel.update(state, true);
        }
      },
    );

    // Add keyboard shortcut for selection mode (Cmd/Ctrl + E)
    this.keyboardHandler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        this.#toggleSelectionMode();
      }
    };
    document.addEventListener('keydown', this.keyboardHandler);

    // Build the entire component
    this.#buildComponent();
  }

  destroy() {
    // Clean up event listeners
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
    if (this.selectionModeListener) {
      aiSelectionModeChanged.remove(this.selectionModeListener);
    }
    if (this.selectionActionListener) {
      aiSelectionActionChanged.remove(this.selectionActionListener);
    }
    if (this.aiViewChangeListener) {
      aiViewChange.remove(this.aiViewChangeListener);
    }
    if (this.unsubscribeSelectedContent) {
      this.unsubscribeSelectedContent();
    }
    if (this.unsubscribeSpatialVisionHistory) {
      this.unsubscribeSpatialVisionHistory();
    }
    this.spatialVisionHistoryPanel?.update?.(
      SpatialVisionState.spatialVision,
      false,
    );
    this.welcomeScreen?.destroy?.();
  }

  #createNewSession(thread = null) {
    const sessionId = Date.now().toString();
    this.chatSessions.set(sessionId, {
      id: sessionId, // Store session ID for reference
      threadId: thread?.id || null,
      chatHistory: thread?.messages || [],
      title: thread?.title || i18next.t('sidebar.aiAnalyzer.new_chat'),
      status: 'idle',
      // Add session-specific state
      selectedFiles: [],
      selectedProjectIds: [],
      selectedTaskIds: [],
      textareaContent: '',
      mentions: [],
      context: [],
      detectSelection: {
        runMode: 'direct',
        image: null,
        images: [],
        sequenceId: null,
        sequenceName: null,
      },
      // Add loading state per session
      isLoading: false,
    });
    return sessionId;
  }

  #switchSession(sessionId) {
    if (!this.chatSessions.has(sessionId)) return;

    // Save current session state before switching (including current file selections)
    if (this.currentSessionId && this.chatSessions.has(this.currentSessionId)) {
      this.#saveCurrentSessionState();
    }

    // if session change and confirmation is shown the hide it
    if (this.confirmationComponent) {
      this.confirmationComponent.hideConfirmation();
    }

    this.currentSessionId = sessionId;
    const session = this.chatSessions.get(sessionId);

    // Update sessionId in analysisResult for selection tracking
    if (this.analysisResult) {
      this.analysisResult.sessionId = sessionId;
    }

    // Restore session state
    this.#restoreSessionState(session);

    // Update UI with session data
    this.analysisResult.displayHistory(session.chatHistory);

    // Restore loading state for this session
    this.#restoreLoadingState(session);

    this.#toggleWelcomeScreen(true);
    this.messagesContainer.dom.scrollTop =
      this.messagesContainer.dom.scrollHeight;

    // Update selection button state as content may have changed
    this.#updateSelectionButtonState();
  }

  #saveCurrentSessionState() {
    if (!this.currentSessionId) return;

    const session = this.chatSessions.get(this.currentSessionId);
    if (!session) return;

    // Stop any ongoing speech recognition
    if (this.reportSection && this.reportSection.stopSpeechRecognition) {
      this.reportSection.stopSpeechRecognition();
    }

    // Save textarea content and mentions
    session.textareaContent = this.reportSection.getPrompt();
    session.mentions = this.reportSection.textarea.mentions || [];
    session.context = this.reportSection.getContext() || [];
    session.detectSelection = this.reportSection.getDetectSelection?.() || {
      runMode: 'direct',
      image: null,
      images: [],
      sequenceId: null,
      sequenceName: null,
    };

    // Save selected files from FoldersState
    session.selectedFiles = this.getSelectedFilesFromFoldersState();

    // Save selected project/task context from AiSelectedItems
    const aiSelection = AiSelectedItems.selectedItems;
    session.selectedProjectIds = aiSelection.projects || [];
    session.selectedTaskIds = aiSelection.tasks || [];

    // Note: isLoading is managed by the request lifecycle, not saved from UI state
  }

  getSelectedFilesFromFoldersState() {
    const selectedFiles = [];

    FoldersState.folders.forEach(folder => {
      folder.urls.forEach(url => {
        if (url.selected) {
          selectedFiles.push({
            path: url.path,
            originalName: url.originalName,
            fileSize: url.fileSize,
            taskId: url.taskId,
            cadFileModelId: url.cadFileModelId,
            reconstructionId: url.reconstructionId,
            folderName: folder.name,
          });
        }
      });
    });

    return selectedFiles;
  }

  #restoreLoadingState(session) {
    if (session.isLoading) {
      // Restore loading UI state
      this.reportSection.setDisabled(true);
      this.reportSection.showLoading();
    } else {
      // Ensure clean state for non-loading sessions
      this.reportSection.setDisabled(false);
      this.reportSection.hideLoading();
    }
  }

  #restoreSessionState(session) {
    // Restore textarea content
    this.reportSection.setPrompt(session.textareaContent || '');

    // Restore mentions and task references
    if (this.reportSection.textarea.mentions !== undefined) {
      this.reportSection.textarea.mentions = session.mentions || [];
    }
    this.reportSection.setContext(session.context || []);
    this.reportSection.setDetectSelection?.(
      session.detectSelection || {
        runMode: 'direct',
        image: null,
        images: [],
        sequenceId: null,
        sequenceName: null,
      },
    );

    // Restore selected files to FoldersState
    // Only update FoldersState if session has files to restore
    // This prevents clearing folders that were set before navigation (e.g., mobile flow)
    if (session.selectedFiles && session.selectedFiles.length > 0) {
      // Convert selected files back to folder structure
      const foldersMap = new Map();

      session.selectedFiles.forEach(file => {
        const folderName = file.folderName || 'Default';
        if (!foldersMap.has(folderName)) {
          foldersMap.set(folderName, {
            name: folderName,
            urls: [],
          });
        }
        foldersMap.get(folderName).urls.push({
          path: file.path,
          originalName: file.originalName,
          fileSize: file.fileSize,
          taskId: file.taskId,
          cadFileModelId: file.cadFileModelId,
          reconstructionId: file.reconstructionId,
          selected: true,
        });
      });

      const folders = Array.from(foldersMap.values());
      FoldersState.updateFolders(folders);
    } else if (this.currentSessionId !== session.id) {
      // Only clear if switching to a different session (not initial load)
      // and the session has no files
      FoldersState.updateFolders([]);
    }

    // Restore selected project/task context to AiSelectedItems
    AiSelectedItems.setSelectedItems({
      projects: session.selectedProjectIds || [],
      tasks: session.selectedTaskIds || [],
    });
  }

  #closeSession(sessionId) {
    // Save current session state if it's the one being closed
    if (sessionId === this.currentSessionId) {
      this.#saveCurrentSessionState();
    }

    this.chatSessions.delete(sessionId);

    // If the closed session was the current one, clear the UI state
    if (sessionId === this.currentSessionId) {
      FoldersState.updateFolders([]);
      AiSelectedItems.setSelectedItems({ projects: [], tasks: [] });
      this.reportSection.clearPrompt();
      this.reportSection.setDetectSelection?.({
        runMode: 'direct',
        image: null,
        images: [],
        sequenceId: null,
        sequenceName: null,
      });
      this.reportSection.setDisabled(false);
      this.reportSection.hideLoading();
      this.confirmationComponent.hideConfirmation();
      this.currentSessionId = null;
    }
  }

  updateFiles(filesData) {
    FoldersState.updateFolders(filesData);
  }

  /**
   * Update the selected project/task context for the current session.
   * Called when the user triggers the AI drawer from the project list bottom bar.
   */
  updateContextSelection(projectIds = [], taskIds = []) {
    // Update global AiSelectedItems state (triggers popover rebuild + counter update)
    AiSelectedItems.setSelectedItems({ projects: projectIds, tasks: taskIds });

    // Sync the current session so selection survives session switching
    if (this.currentSessionId && this.chatSessions.has(this.currentSessionId)) {
      const session = this.chatSessions.get(this.currentSessionId);
      session.selectedProjectIds = projectIds;
      session.selectedTaskIds = taskIds;
    }

    // Explicitly refresh the counter in case the subscriber hasn't fired yet
    this.reportSection.updateProjectCountUIValue();
  }

  async loadThread(thread) {
    const sessionId = this.#createNewSession(thread);
    this.#switchSession(sessionId);
    this.chatTabs.addTab(sessionId, thread.title);
    this.analysisResult.displayHistory(thread.messages);
    this.#toggleWelcomeScreen();
    // Switch back to chat view when loading a thread
    this.#showChatView();
    this.messagesContainer.dom.scrollTop =
      this.messagesContainer.dom.scrollHeight;
  }

  copyBookmark(message) {
    this.reportSection.setPrompt(message);
    // Switch back to chat view
    this.#showChatView();
  }

  async #handleConfirmAction(token) {
    try {
      const result = await confirmAiAction(token);

      if (result.status === 200) {
        this.#addMessageToHistory({
          role: 'system',
          content: 'Action confirmed successfully.',
          createdAt: new Date().toISOString(),
          sessionId: this.currentSessionId,
        });

        if (result.body?.projectIds?.length || result.body?.taskIds?.length) {
          const { folders, tasks } = ProjectsState.projects;
          const updatedFolders = folders.filter(
            folder => !result.body?.projectIds?.includes(folder.id),
          );
          const updatedTasks = tasks.filter(
            task => !result.body?.taskIds?.includes(task.id),
          );
          ProjectsState.setProjects({
            ...ProjectsState.projects,
            folders: updatedFolders,
            tasks: updatedTasks,
          });
        }
        if (result.body?.commentIds) {
          for (const commentId of result.body.commentIds) {
            // check CadFileState.cadfile contains the comment id then if yes do the actions below
            if (CadFileState.cadfile && CadFileState.cadfile.comments) {
              const comment = CadFileState.cadfile.comments.find(
                c => c.commentId === commentId,
              );
              if (comment) {
                deleteTag.dispatch(commentId);
                const data = {
                  commentId: commentId,
                  cadFileId: Number(CadFileState.cadfile.cadFileID),
                };
                _socket.emit('send_comment_updates', data, 'comment_deleted');
              }
            }
          }
        }
      } else {
        this.#addMessageToHistory({
          role: 'system',
          content: 'Failed to confirm action.',
          createdAt: new Date().toISOString(),
          sessionId: this.currentSessionId,
        });
      }
    } catch (error) {
      this.#addMessageToHistory({
        role: 'system',
        content: 'Failed to confirm action.',
        createdAt: new Date().toISOString(),
        sessionId: this.currentSessionId,
      });
      console.error('Error confirming action:', error);
    }
  }

  async #handleRejectAction() {
    // Add a cancellation message to the chat history
    this.#addMessageToHistory({
      role: 'system',
      content:
        'Action canceled. The requested operations have been declined and will not be performed.',
      createdAt: new Date().toISOString(),
      sessionId: this.currentSessionId,
    });
  }

  async #onCreateReport() {
    if (!this.currentSessionId) return;

    if (this.reportSection.getMode() === 'detect') {
      await this.#onCreateDetectReport();
      return;
    }

    // Store the session ID that initiated this request
    const requestSessionId = this.currentSessionId;
    const currentSession = this.chatSessions.get(requestSessionId);

    // Save current session state before processing (including current file selections)
    this.#saveCurrentSessionState();

    // Mark this session as loading
    currentSession.isLoading = true;

    try {
      this.reportSection.setDisabled(true);
      this.reportSection.showLoading();
      this.chatTabs.setTabStatus(requestSessionId, 'loading');

      const prompt = this.reportSection.getPrompt();

      if (prompt === '') {
        toast.error(i18next.t('sidebar.aiAnalyzer.toast.no_prompt'));
        return;
      }

      // Get selected files from current session
      const files = currentSession.selectedFiles || [];

      // max 10 files allowed
      if (files.length > 10) {
        toast.error(i18next.t('sidebar.aiAnalyzer.toast.max_files_exceeded'));
        return;
      }

      // Create a thread before freezing report metadata so the generated title
      // is also available for future PDF downloads from history.
      if (!currentSession.threadId) {
        const thread = await createThread({
          prompt,
        });
        currentSession.threadId = thread.body.id;
        currentSession.title = thread.body.title;

        this.chatTabs.updateTabTitle(requestSessionId, thread.body.title);
      }

      const reportContext = await this.#getCurrentReportContextWithQuotations();

      // Add user message to history
      this.#addMessageToHistory({
        role: 'user',
        content: prompt,
        createdAt: new Date().toISOString(),
        sessionId: requestSessionId, // Pass the session ID
        reportContext,
      });

      // Add assistant message with loading indicator
      this.#addMessageToHistory({
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        sessionId: requestSessionId,
        isStreaming: true,
        reportContext,
      });

      // const mentionedUsers = this.reportSection.textarea.mentions || [];
      // // check prompt and add [user_id] after each mention
      // if (mentionedUsers.length > 0) {
      //   prompt = mentionedUsers.reduce((acc, user) => {
      //     return acc.replace(
      //       `${user.name}`,
      //       `${user.name}[${user.organizationUserId}]`,
      //     );
      //   }, prompt);
      // }

      // const context = currentSession.context || [];

      // check prompt and add {TASK:task_name, task_id} after each mention
      // if (context.length > 0) {
      //   prompt = context.reduce((acc, task) => {
      //     return acc.replace(
      //       `${task.name}`,
      //       `[${task.type.toUpperCase()}:${task.name}, ${task.id}]`,
      //     );
      //   }, prompt);
      // }

      // Extract command context for backend processing
      // const commandContext = this.reportSection.findCommand(prompt);

      // Get current AI mode from report section
      const aiMode = this.reportSection.getMode();
      const cookies = new UniversalCookie();
      const response = await fetch(APIURL + '/ai/inference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': cookies.get('token'),
        },
        body: JSON.stringify({
          prompt,
          files,
          history: currentSession.chatHistory.slice(0, -1),
          threadId: currentSession.threadId,
          organizationId: getCurrentOrganizationId(),
          mode: aiMode, // 'work' for project management, 'chat' for conversational AI
          selectedItems: AiSelectedItems.selectedItems,
          reportContext,
        }),
      });

      // Create a reader to read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the raw bytes into a string
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process the buffer line by line (NDJSON)
        const lines = buffer.split('\n');

        // Keep the last part in the buffer (it might be incomplete)
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue; // Skip empty lines

          try {
            const data = JSON.parse(line);
            this.#handleStreamEvent(data, requestSessionId, prompt, files);
          } catch (e) {
            console.error('Error parsing JSON chunk', e);
          }
        }
      }

      // Clear current session's textarea content after successful submission
      currentSession.textareaContent = '';
      currentSession.mentions = [];
      currentSession.context = [];
    } catch (error) {
      console.error('Error creating report:', error);
      toast.error(i18next.t('sidebar.aiAnalyzer.toast.error'));
      this.chatTabs.setTabStatus(requestSessionId, 'error');
      // Mark session as no longer loading on error
      currentSession.isLoading = false;
    } finally {
      // Mark session as no longer loading (also handles success case)
      currentSession.isLoading = false;

      // Only update UI if we're still on the same session
      if (this.currentSessionId === requestSessionId) {
        this.reportSection.clearPrompt();
        this.reportSection.setDisabled(false);
        this.reportSection.hideLoading();
        this.reportSection.focusInput();
      }
    }
  }

  async #onCreateDetectReport() {
    if (!this.currentSessionId) return;

    const requestSessionId = this.currentSessionId;
    const currentSession = this.chatSessions.get(requestSessionId);
    if (!currentSession) return;

    this.#saveCurrentSessionState();

    const detectSelection =
      currentSession.detectSelection ||
      this.reportSection.getDetectSelection?.() ||
      {};
    const selectedImage = detectSelection.image;
    const selectedImages =
      detectSelection.images?.length > 0
        ? detectSelection.images
        : selectedImage
          ? [selectedImage]
          : [];
    const isSequenceMode = detectSelection.runMode === 'sequence';
    const { accepted: sizeEligibleImages, rejected: oversizedSelections } =
      partitionVlmImages(selectedImages);
    let runImages = sizeEligibleImages;
    const runSelection = {
      ...detectSelection,
      image: runImages[0] || null,
      images: runImages,
    };
    if (oversizedSelections.length) {
      this.reportSection.showDetectImageValidation(
        oversizedSelections,
        runImages.length,
      );
      currentSession.detectSelection = runSelection;
      this.reportSection.setDetectSelection(runSelection);
    } else this.reportSection.clearDetectImageValidation();
    const isOhiveUnsupportedBatch =
      !isSequenceMode &&
      SpatialVisionState.spatialVision.selectedServiceTier !== 'cloud' &&
      runImages.length > 1;
    const prompt = isSequenceMode ? '' : this.reportSection.getPrompt();
    const sequenceId = detectSelection.sequenceId;
    const sequenceName =
      detectSelection.sequenceName ||
      this.#getSpatialVisionSequenceName(sequenceId);

    if (!runImages.some(image => image?.path || image?.file)) {
      if (!oversizedSelections.length) {
        toast.error(
          i18next.t('sidebar.aiAnalyzer.detect.no_image') ||
            'Select an image before running detection.',
        );
      }
      return;
    }

    if (isOhiveUnsupportedBatch) {
      toast.warning(
        'O-HIVE VLM PRO can process only one image at a time. Switch to O-HIVE VLM CLOUD for batch detection or keep one image selected.',
      );
      return;
    }

    if (isSequenceMode && !sequenceId) {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.detect.no_sequence') ||
          'Create or select a sequence before running detection.',
      );
      return;
    }

    if (!isSequenceMode && prompt === '') {
      toast.error(
        i18next.t('sidebar.aiAnalyzer.detect.no_prompt') ||
          'Describe what to detect.',
      );
      return;
    }

    currentSession.isLoading = true;
    currentSession.detectSelection = runSelection;

    try {
      this.reportSection.setDisabled(true);
      this.reportSection.showLoading();
      this.chatTabs.setTabStatus(requestSessionId, 'loading');

      const title =
        sequenceName ||
        prompt.slice(0, 40) ||
        i18next.t('sidebar.aiAnalyzer.mode.detect') ||
        'Detect';
      currentSession.title = title;
      this.chatTabs.updateTabTitle(requestSessionId, title);

      const preparedImages = await Promise.all(
        runImages.map(async image => ({
          image,
          file: image.file
            ? image.file
            : await this.#createFileFromUrl(image.path, image.originalName),
        })),
      );
      const validPreparedImages = preparedImages.filter(
        prepared => !isVlmImageOversized(prepared.file),
      );
      const oversizedPreparedImages = preparedImages
        .filter(prepared => isVlmImageOversized(prepared.file))
        .map(prepared => ({
          ...prepared.image,
          file: prepared.file,
          fileSize: prepared.file.size,
        }));
      if (oversizedPreparedImages.length) {
        this.reportSection.showDetectImageValidation(
          oversizedPreparedImages,
          validPreparedImages.length,
        );
      }
      runImages = validPreparedImages.map(prepared => prepared.image);
      const files = validPreparedImages.map(prepared => prepared.file);
      const validatedSelection = {
        ...runSelection,
        image: runImages[0] || null,
        images: runImages,
      };
      currentSession.detectSelection = validatedSelection;
      this.reportSection.setDetectSelection(validatedSelection);
      if (!files.length) return;

      this.#addMessageToHistory({
        role: 'user',
        content: prompt || `Run ${sequenceName || 'selected sequence'}`,
        createdAt: new Date().toISOString(),
        sessionId: requestSessionId,
        spatialVisionInput: {
          imageName:
            runImages.length > 1
              ? `${runImages.length} images`
              : runImages[0]?.originalName,
          sequenceName,
        },
        hideActions: true,
      });

      const automatedTagsSources = runImages.map(image =>
        this.#automatedTagsSourceForImage(image),
      );
      console.info('[AiAnalyzer][SpatialVision] Queueing detection job', {
        mode: isSequenceMode ? 'sequence' : 'direct',
        sequenceId: isSequenceMode ? sequenceId : null,
        imageCount: files.length,
        promptLength: prompt.length,
      });
      console.info(
        '[DEBUG-auto-tags] Source images before queue',
        automatedTagsSources.map(debugAutomatedTagSource),
      );

      const job = isSequenceMode
        ? await SpatialVisionState.runSequenceJob({
            sequenceId,
            files,
            prompt: sequenceName || prompt || 'Run sequence',
          })
        : await SpatialVisionState.runDirectJob({
            prompt,
            files,
          });

      console.info('[AiAnalyzer][SpatialVision] Detection job queued', {
        jobId: job?.id,
        status: job?.status,
        imageCount: job?.imageResults?.length || job?.items?.length || 0,
      });

      this.#addMessageToHistory({
        role: 'assistant',
        content:
          i18next.t('sidebar.aiAnalyzer.detect.result') ||
          'Spatial Vision result',
        createdAt: new Date().toISOString(),
        sessionId: requestSessionId,
        spatialVisionJob: job,
        automatedTagsSources,
        hideActions: true,
      });

      this.#createAutomatedTagsWhenJobCompletes({
        jobId: job?.id,
        sourceImages: automatedTagsSources,
      });

      currentSession.textareaContent = '';
      currentSession.mentions = [];
      currentSession.context = [];
      currentSession.detectSelection = {
        ...runSelection,
        image: null,
        images: [],
      };
      this.chatTabs.setTabStatus(requestSessionId, 'success');
    } catch (error) {
      console.error('Error running Spatial Vision from AI Analyzer:', error);
      toast.error(
        i18next.t('sidebar.aiAnalyzer.detect.error') ||
          'Detection failed. Please try again.',
      );
      this.chatTabs.setTabStatus(requestSessionId, 'error');
      currentSession.isLoading = false;
    } finally {
      currentSession.isLoading = false;
      if (this.currentSessionId === requestSessionId) {
        this.reportSection.clearPrompt();
        this.reportSection.setDetectSelection(currentSession.detectSelection);
        this.reportSection.setDisabled(false);
        this.reportSection.hideLoading();
        this.reportSection.focusInput();
      }
    }
  }

  async #createAutomatedTagsWhenJobCompletes({ jobId, sourceImages }) {
    if (!jobId) return;
    try {
      const job = await this.#waitForSpatialVisionJob(jobId);
      if (!job || !AUTOMATED_TAG_JOB_TERMINAL_STATUSES.has(job.status)) return;
      await this.#createAutomatedTagsForJobResults(job, sourceImages);
    } catch (error) {
      console.error('Failed to create automated tags from AI Analyzer:', error);
      toast.error('Automated tags could not be created.');
    }
  }

  async #waitForSpatialVisionJob(jobId) {
    for (let attempt = 0; attempt < AUTOMATED_TAG_JOB_MAX_POLLS; attempt += 1) {
      const currentJob = this.#findSpatialVisionJob(jobId);
      if (
        currentJob &&
        AUTOMATED_TAG_JOB_TERMINAL_STATUSES.has(currentJob.status)
      ) {
        return currentJob;
      }

      const jobs = await SpatialVisionState.refreshJobs({ activeOnly: true });
      const refreshedJob =
        jobs.find(job => String(job.id) === String(jobId)) ||
        this.#findSpatialVisionJob(jobId);
      if (
        refreshedJob &&
        AUTOMATED_TAG_JOB_TERMINAL_STATUSES.has(refreshedJob.status)
      ) {
        return refreshedJob;
      }

      await this.#sleep(AUTOMATED_TAG_JOB_POLL_INTERVAL_MS);
    }
    return this.#findSpatialVisionJob(jobId);
  }

  #findSpatialVisionJob(jobId) {
    return SpatialVisionState.spatialVision.jobs.find(
      job => String(job.id) === String(jobId),
    );
  }

  async #createAutomatedTagsForJobResults(job, sourceImages = []) {
    const imageResults = job.imageResults || job.items || [];
    const summary = {
      createdCount: 0,
      failedCount: 0,
      totalCount: 0,
      skippedCount: 0,
    };

    for (let index = 0; index < imageResults.length; index += 1) {
      const item = imageResults[index];
      const status = item.status || job.status;
      const detections = item.detections || [];
      const conditionResults =
        item.conditionResults ||
        item.condition_results ||
        item.resultMetadata?.conditionResults ||
        item.resultMetadata?.condition_results ||
        [];
      if (
        status !== 'completed' ||
        (!detections.length && !conditionResults.length)
      ) {
        continue;
      }

      const sourceImage = this.#sourceImageForJobItem(
        item,
        index,
        sourceImages,
      );
      if (!this.#isAutoTaggableSourceImage(sourceImage)) {
        continue;
      }

      try {
        const result = await createAutomatedTagsForExistingImage({
          item,
          sourceImage,
        });
        summary.createdCount += result.createdCount;
        summary.failedCount += result.failedCount;
        summary.totalCount += result.totalCount;
      } catch (error) {
        console.error('Automated tag creation failed for image result:', error);
        summary.failedCount += detections.length || 1;
        summary.totalCount += detections.length;
      }
    }

    this.#showAutomatedTagsSummary(summary);
  }

  #sourceImageForJobItem(item, index, sourceImages) {
    const itemIndex = Number(item?.itemIndex);
    if (Number.isInteger(itemIndex) && sourceImages[itemIndex]) {
      return sourceImages[itemIndex];
    }
    return sourceImages[index] || null;
  }

  #automatedTagsSourceForImage(image) {
    const isUpload = image?.source === 'upload' || Boolean(image?.file);
    const folderFile = this.#selectedFolderFileForSourceImage(image);
    const viewerModel = isUpload
      ? null
      : this.#viewerModelForSourceImage(image);
    const path = image?.path || image?.filePath || folderFile?.path || null;
    return {
      ...image,
      source: image?.source || (isUpload ? 'upload' : 'project'),
      taskId:
        image?.taskId ||
        (!isUpload
          ? folderFile?.taskId ||
            CadFileState.cadfile?.cadFileID ||
            CadFileState.cadfile?.id
          : null) ||
        null,
      cadFileModelId:
        getCadFileModelId(image) ||
        (!isUpload
          ? getCadFileModelId(folderFile) || getCadFileModelId(viewerModel)
          : null),
      reconstructionId:
        getReconstructionId(image) ||
        (!isUpload
          ? getReconstructionId(folderFile) || getReconstructionId(viewerModel)
          : null),
      path,
      filePath: image?.filePath || folderFile?.filePath || path,
    };
  }

  #selectedFolderFileForSourceImage(image) {
    const path = image?.path || image?.filePath;
    if (!path) return null;
    return (
      FoldersState.folders
        .flatMap(folder => folder.urls || [])
        .find(url => url.path === path || url.filePath === path) || null
    );
  }

  #viewerModelForSourceImage(image) {
    const path = image?.path || image?.filePath;
    const reconstructionId = getReconstructionId(image);
    const cadFileModelId = getCadFileModelId(image);
    return (
      (AvailableModelState.availableModel || []).find(model => {
        const sameCadFileModel =
          cadFileModelId &&
          String(getCadFileModelId(model)) === String(cadFileModelId);
        const sameReconstruction =
          reconstructionId &&
          String(getReconstructionId(model)) === String(reconstructionId);
        const samePath = path && model?.filePath === path;
        return sameCadFileModel || sameReconstruction || samePath;
      }) || null
    );
  }

  #isAutoTaggableSourceImage(sourceImage) {
    return isViewerFileSource(sourceImage);
  }

  #showAutomatedTagsSummary(summary) {
    if (summary.failedCount > 0) {
      toast.error(
        'Automated tags failed',
        `${summary.failedCount} tag${
          summary.failedCount === 1 ? '' : 's'
        } could not be created.`,
      );
    }
  }

  #sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  #cacheBustedUrl(url) {
    const separator = String(url).includes('?') ? '&' : '?';
    return `${url}${separator}cors_bust=${Date.now()}`;
  }

  async #createFileFromUrl(url, filename = 'image') {
    const response = await fetch(this.#cacheBustedUrl(url), {
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`Could not load image (${response.status})`);
    }

    const blob = await response.blob();
    const extension = filename.includes('.')
      ? ''
      : this.#extensionFromBlob(blob);
    return new File([blob], `${filename}${extension}`, {
      type: blob.type || 'application/octet-stream',
    });
  }

  #extensionFromBlob(blob) {
    if (!blob?.type?.startsWith('image/')) return '';
    const subtype = blob.type.split('/')[1];
    return subtype ? `.${subtype}` : '';
  }

  #getSpatialVisionSequenceName(sequenceId) {
    if (!sequenceId) return null;
    return (
      SpatialVisionState.spatialVision.sequences.find(
        sequence => String(sequence.id) === String(sequenceId),
      )?.name || null
    );
  }

  /**
   * Check if the response content is an action response (JSON with requiresAction)
   */
  #isActionResponse(content) {
    if (!content || typeof content !== 'string') return false;
    try {
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed);
        return parsed.requiresAction && parsed.action;
      }
    } catch (e) {
      // Not valid JSON
    }
    return false;
  }

  /**
   * Handle retry request after action completion (e.g., Google OAuth)
   */
  async #handleRetryRequest(pendingRequest) {
    if (!pendingRequest) return;

    const { files, sessionId, threadId, reportContext } = pendingRequest;

    // Switch to the correct session if needed
    if (sessionId && sessionId !== this.currentSessionId) {
      this.#switchSession(sessionId);
    }

    const currentSession = this.chatSessions.get(this.currentSessionId);
    if (!currentSession) return;

    // Store the session ID that initiated this request
    const requestSessionId = this.currentSessionId;

    // Mark this session as loading
    currentSession.isLoading = true;

    // Use a continuation message instead of repeating the original prompt
    const continuationPrompt =
      'I have connected my Google account. Please continue with my previous request.';

    try {
      this.reportSection.setDisabled(true);
      this.reportSection.showLoading();
      this.chatTabs.setTabStatus(requestSessionId, 'loading');

      // Add assistant message with loading indicator for streaming
      this.#addMessageToHistory({
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        sessionId: requestSessionId,
        isStreaming: true,
        reportContext,
      });

      // Get current AI mode from report section
      const aiMode = this.reportSection.getMode();
      const cookies = new UniversalCookie();
      const response = await fetch(APIURL + '/ai/inference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': cookies.get('token'),
        },
        body: JSON.stringify({
          prompt: continuationPrompt,
          files: files || [],
          history: currentSession.chatHistory.slice(0, -1), // Exclude the last assistant message we just added
          threadId: threadId || currentSession.threadId,
          organizationId: getCurrentOrganizationId(),
          mode: aiMode, // 'work' for project management, 'chat' for conversational AI
          selectedItems: AiSelectedItems.selectedItems,
          reportContext,
        }),
      });

      // Create a reader to read the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the raw bytes into a string
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process the buffer line by line (NDJSON)
        const lines = buffer.split('\n');

        // Keep the last part in the buffer (it might be incomplete)
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.trim()) continue; // Skip empty lines

          try {
            const data = JSON.parse(line);
            this.#handleStreamEvent(
              data,
              requestSessionId,
              continuationPrompt,
              files || [],
            );
          } catch (e) {
            console.error('Error parsing JSON chunk', e);
          }
        }
      }
    } catch (error) {
      console.error('Error retrying request:', error);
      toast.error(i18next.t('sidebar.aiAnalyzer.toast.error'));
      this.chatTabs.setTabStatus(requestSessionId, 'error');
      // Mark session as no longer loading on error
      currentSession.isLoading = false;
    } finally {
      // Mark session as no longer loading (also handles success case)
      currentSession.isLoading = false;

      // Only update UI if we're still on the same session
      if (this.currentSessionId === requestSessionId) {
        this.reportSection.setDisabled(false);
        this.reportSection.hideLoading();
        this.reportSection.focusInput();
      }
    }
  }

  #toggleWelcomeScreen(forceCheck = false) {
    if (!this.currentSessionId && !forceCheck) return;

    // check if the current session is empty
    const currentSession = this.chatSessions.get(this.currentSessionId);
    const hasContent = currentSession && currentSession.chatHistory.length > 0;

    if (!hasContent) {
      this.welcomeScreen.removeClass('hidden');
      this.analysisResult.addClass('hidden');
    } else {
      this.welcomeScreen.addClass('hidden');
      this.analysisResult.removeClass('hidden');
    }
  }

  #handleStreamEvent(json, requestSessionId, prompt, files) {
    switch (json.type) {
      case 'status':
        // Update the loading status text in the AI message
        this.analysisResult.updateLoadingStatus(json.message);
        break;

      case 'chunk':
        // Stream content into the message element
        this.analysisResult.appendToStreamingMessage(json.content);
        // Auto-scroll to bottom
        this.messagesContainer.dom.scrollTop =
          this.messagesContainer.dom.scrollHeight;
        break;

      case 'metadata':
        const metaData = json.data;

        // if we are in work mode we get the response from 'text' metadata field
        if (metaData.text && this.reportSection.getMode() === 'work') {
          // Append the full text at once for work mode
          this.analysisResult.appendToStreamingMessage(metaData.text);

          // Check if response requires action (like Google connect) and store pending request
          if (this.#isActionResponse(metaData.text)) {
            const currentSession = this.chatSessions.get(requestSessionId);
            this.analysisResult.setPendingRequest({
              prompt,
              files,
              sessionId: requestSessionId,
              threadId: currentSession.threadId,
              reportContext:
                currentSession.chatHistory?.[
                  currentSession.chatHistory.length - 1
                ]?.reportContext,
            });
          }
        }

        // Mark streaming as complete
        this.analysisResult.finalizeStreamingMessage();

        // Check for confirmation tokens
        if (metaData.confirmToken) {
          this.confirmationComponent.showConfirmation(
            metaData.confirmToken,
            requestSessionId,
          );
        }

        const tools = metaData.usedTools || [];
        console.log('Tools used in response:', tools);

        // For update_task: do a targeted per-project refresh using the task IDs
        // from the tool args so we get fresh columnValues from the detail endpoint.
        const updateTaskTools = tools.filter(
          tool => tool?.name === 'update_task',
        );
        if (updateTaskTools.length > 0) {
          const taskIds = updateTaskTools.flatMap(tool =>
            (tool.args?.updates || []).map(u => u.taskId).filter(Boolean),
          );
          if (taskIds.length > 0) {
            ProjectsState.refreshProjectsByTaskIds(taskIds);
          }
        }

        // For create/structural operations reload the full project list
        if (
          tools.some(
            tool =>
              tool?.name === 'create_project' ||
              tool?.name === 'update_project' ||
              tool?.name === 'create_column' ||
              tool?.name === 'create_task' ||
              tool?.name === 'create_meeting',
          )
        ) {
          const orgId = getCurrentOrganizationId();
          ProjectsState.getProjectsAndTasks(orgId);
        }

        // Check if a meeting was created and if it resulted in an internal calendar entry
        const createMeetingTool = tools.find(
          tool => tool?.name === 'create_meeting',
        );
        if (
          createMeetingTool &&
          createMeetingTool.output?.details?.internalCalendar?.organizer
        ) {
          projectListUpdated.dispatch();
        }

        // Update user credits
        if (metaData.credits != null)
          updateCurrentSpaceCredits(metaData.credits);

        this.chatTabs.setTabStatus(requestSessionId, 'success');
        break;

      case 'links':
        // Store links data for use when rendering markdown
        // Extract all properties except 'type' as these are the link IDs
        const linksData = { ...json };
        delete linksData.type;
        this.analysisResult.setLinksMap(linksData);
        break;

      case 'images':
        // Store images data for use when rendering markdown
        const imagesData = { ...json };
        delete imagesData.type;
        this.analysisResult.setImagesMap(imagesData);
        break;

      case 'products':
        // Store products data for use when rendering markdown
        // Products data comes as a single object with type, platform, query, and products array
        this.analysisResult.setProductsData(json.products);
        break;

      case 'done':
        // Finalize the streaming message (handles empty content case)
        this.analysisResult.finalizeStreamingMessage();

        // Ensure loading state is cleared
        const currentSession = this.chatSessions.get(requestSessionId);
        if (currentSession) {
          currentSession.isLoading = false;
        }

        // Update UI if we're still on the same session
        if (this.currentSessionId === requestSessionId) {
          this.reportSection.hideLoading();
          this.reportSection.setDisabled(false);
        }
        break;

      default:
        console.warn('Unknown stream event type:', json.type);
        break;
    }
  }

  #addMessageToHistory(message) {
    const sessionId = message.sessionId || this.currentSessionId;
    if (!sessionId || !this.chatSessions.has(sessionId)) return;

    const session = this.chatSessions.get(sessionId);
    const { sessionId: _, ...messageWithoutSessionId } = message; // Remove sessionId from message
    session.chatHistory.push(messageWithoutSessionId);

    // Only update UI if we're on the same tab
    if (sessionId === this.currentSessionId) {
      this.#toggleWelcomeScreen(true);
      this.analysisResult.addMessage(messageWithoutSessionId);
      this.messagesContainer.dom.scrollTop =
        this.messagesContainer.dom.scrollHeight;
      // Update selection button state as content has changed
      this.#updateSelectionButtonState();
    }
  }

  #updateCreditsLabel = () => {
    this.creditsCount.setTextContent(`${getCurrentSpaceCredits() || 0}`);
  };

  #getCurrentOrganizationForReport() {
    const organizationId = getCurrentOrganizationId();
    if (!organizationId) return null;

    return (OrganizationState.organizations || []).find(
      org => String(org.organizationId) === String(organizationId),
    );
  }

  #getCurrentTasksForReport() {
    const currentSession = this.chatSessions.get(this.currentSessionId);
    const selectedTaskIds =
      currentSession?.selectedTaskIds?.length > 0
        ? currentSession.selectedTaskIds
        : AiSelectedItems.selectedItems.tasks || [];

    if (!selectedTaskIds.length) return [];

    return selectedTaskIds
      .map(taskId => {
        const task = ProjectsState.projects?.tasks?.find(
          projectTask => String(projectTask.id) === String(taskId),
        );
        if (!task) return null;

        const project = ProjectsState.projects?.folders?.find(
          folder => String(folder.id) === String(task.parentId),
        );

        return {
          id: task.id,
          name: task.name,
          description: task.description || '',
          projectId: task.parentId,
          projectName: project?.name || task.parentFolderName || '',
        };
      })
      .filter(Boolean);
  }

  #getCurrentProjectsForReport() {
    const currentSession = this.chatSessions.get(this.currentSessionId);
    const selectedProjectIds =
      currentSession?.selectedProjectIds?.length > 0
        ? currentSession.selectedProjectIds
        : AiSelectedItems.selectedItems.projects || [];

    if (!selectedProjectIds.length) return [];

    return selectedProjectIds
      .map(projectId => {
        const project = ProjectsState.projects?.folders?.find(
          folder => String(folder.id) === String(projectId),
        );
        if (!project) return null;

        return {
          id: project.id,
          name: project.name,
        };
      })
      .filter(Boolean);
  }

  #getLogoUrlForReport(organization) {
    const logoKey =
      organization?.logoKey || organization?.organization?.logoKey;
    if (!logoKey) return null;

    if (logoKey.startsWith('http://') || logoKey.startsWith('https://')) {
      return this.#isPdfSafeLogoUrl(logoKey) ? logoKey : null;
    }

    const logoUrl = logoKey.startsWith('/')
      ? `${S3URL}${logoKey.slice(1)}`
      : `${S3URL}${logoKey}`;

    return this.#isPdfSafeLogoUrl(logoUrl) ? logoUrl : null;
  }

  #isPdfSafeLogoUrl(logoUrl) {
    if (!logoUrl) return false;
    if (logoUrl.startsWith('data:') || logoUrl.startsWith('blob:')) return true;

    try {
      const url = new URL(logoUrl, window.location.origin);
      return url.origin === window.location.origin;
    } catch (error) {
      console.warn('Skipping PDF logo with invalid URL:', error);
      return false;
    }
  }

  #getCurrentReportContext() {
    const currentSession = this.chatSessions.get(this.currentSessionId);
    const organization = this.#getCurrentOrganizationForReport();
    const user = UserState.user || {};
    const userName =
      user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      user.name ||
      user.email ||
      '';

    return {
      title: currentSession?.title || 'AI Analysis Report',
      organization: organization
        ? {
            id: organization.organizationId,
            name: organization.name || organization.organization?.name || '',
            logoKey:
              organization.logoKey || organization.organization?.logoKey || '',
          }
        : null,
      user: {
        name: userName,
        email: user.email || '',
      },
      projects: this.#getCurrentProjectsForReport(),
      tasks: this.#getCurrentTasksForReport(),
      generatedAt: new Date(),
    };
  }

  async #getCurrentReportContextWithQuotations() {
    return this.#enrichReportContextWithQuotations(
      this.#getCurrentReportContext(),
    );
  }

  #stripHtml(value = '') {
    if (!value) return '';

    const container = document.createElement('div');
    container.innerHTML = String(value);
    return (container.textContent || container.innerText || '').trim();
  }

  #formatQuotationAddress(address) {
    if (!address) return '';

    return [
      address.address,
      address.details,
      address.city,
      address.province,
      address.country,
    ]
      .filter(Boolean)
      .join(', ');
  }

  #formatQuotationContact(quotation) {
    if (!quotation) return '';

    const name = [quotation.firstName, quotation.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return [name, quotation.phone, quotation.email].filter(Boolean).join(' | ');
  }

  #normalizeQuotationForReport(task, quotation) {
    if (!quotation) return null;

    const address = this.#formatQuotationAddress(quotation.quotationAddress);
    const contact = this.#formatQuotationContact(quotation);
    const description = this.#stripHtml(
      quotation.description || task.description || '',
    );

    return {
      taskId: task.id,
      taskTitle: task.name,
      title: quotation.title || task.name,
      description,
      address,
      contact,
    };
  }

  async #enrichReportContextWithQuotations(reportContext = null) {
    const context = reportContext || this.#getCurrentReportContext();
    const tasks = context.tasks || [];
    if (!tasks.length) return context;

    const quotationDetails = await Promise.all(
      tasks.map(async task => {
        try {
          const result = await getQuotation(task.id);
          return this.#normalizeQuotationForReport(task, result?.response);
        } catch (error) {
          console.warn('Failed to fetch quotation for AI PDF context:', error);
          return null;
        }
      }),
    );

    const details = quotationDetails.filter(Boolean);
    if (!details.length) return context;

    return {
      ...context,
      tasks: tasks.map(task => {
        const quotation = details.find(
          item => String(item.taskId) === String(task.id),
        );
        return quotation
          ? {
              ...task,
              description: quotation.description,
              quotation,
            }
          : task;
      }),
      quotationDetails: details,
    };
  }

  #getPdfReportOptions(reportContext = null) {
    const fallbackContext = this.#getCurrentReportContext();
    const context = reportContext || fallbackContext;
    const organization = context.organization;
    const organizationForLogo = organization
      ? {
          organizationId: organization.id,
          name: organization.name,
          logoKey: organization.logoKey,
        }
      : this.#getCurrentOrganizationForReport();

    return {
      title: context.title || fallbackContext.title || 'AI Analysis Report',
      organizationName: organization?.name || 'Personal',
      logoUrl: this.#getLogoUrlForReport(organizationForLogo),
      userName: context.user?.name || '',
      userEmail: context.user?.email || '',
      taskNames: (context.tasks || []).map(task => task.name).filter(Boolean),
      quotationDetails: context.quotationDetails || [],
      projectNames: (context.projects || [])
        .map(project => project.name)
        .filter(Boolean),
      generatedAt: context.generatedAt || new Date(),
    };
  }

  #getReportTasksForStats(reportContext = null) {
    const context = reportContext || this.#getCurrentReportContext();
    const selectedTaskIds = new Set(
      (context.tasks || []).map(task => String(task.id)),
    );
    const selectedProjectIds = new Set(
      (context.projects || []).map(project => String(project.id)),
    );

    if (!selectedTaskIds.size && !selectedProjectIds.size) return [];

    const tasksById = new Map();
    (ProjectsState.projects?.tasks || []).forEach(task => {
      const isSelectedTask = selectedTaskIds.has(String(task.id));
      const isInSelectedProject = selectedProjectIds.has(String(task.parentId));
      if (isSelectedTask || isInSelectedProject) {
        tasksById.set(String(task.id), task);
      }
    });

    return [...tasksById.values()];
  }

  #getTaskColumnValue(task, column) {
    if (!task || !column) return '';

    const columnValue = task.columnValues?.find(
      value =>
        String(value.columnId) === String(column.id) ||
        String(value.columnId) === String(column.key),
    );

    if (columnValue?.value !== undefined && columnValue?.value !== null) {
      return columnValue.value;
    }

    return task[column.key] || '';
  }

  #getStatusOption(column, value) {
    const options = column?.options || {};
    const option = options[value];
    if (!option) {
      return {
        value,
        label: value ? String(value) : 'No status',
        color: '#94a3b8',
      };
    }

    return {
      value,
      label: typeof option === 'string' ? option : option.label || value,
      color: typeof option === 'string' ? '#94a3b8' : option.color || '#94a3b8',
    };
  }

  #isCompletedStatus(status) {
    const normalized = String(status?.value || status?.label || '')
      .trim()
      .toLowerCase();
    return normalized === 'completed' || normalized === 'complete';
  }

  #resolveMemberName(uid) {
    const member = (MemberState.members || []).find(
      user => String(user.uid) === String(uid),
    );
    return (
      member?.full_name ||
      [member?.first_name, member?.last_name].filter(Boolean).join(' ') ||
      member?.name ||
      member?.email ||
      uid
    );
  }

  #buildPdfReportStats(reportContext = null) {
    const tasks = this.#getReportTasksForStats(reportContext);
    if (!tasks.length) return null;

    const statusMap = new Map();
    const assigneeMap = new Map();
    const statusOrder = [];
    let completedTasks = 0;

    tasks.forEach(task => {
      const columns = getVisibleColumns(task.parentId);
      const statusColumn = columns.find(
        column => column.type === 'select' || column.key === 'select',
      );
      const peopleColumn = columns.find(column => column.type === 'people');
      let taskStatus = null;

      if (statusColumn) {
        const statusValue = this.#getTaskColumnValue(task, statusColumn);
        const status = this.#getStatusOption(statusColumn, statusValue);
        const key = status.value || status.label;
        if (!statusOrder.includes(key)) statusOrder.push(key);
        const previous = statusMap.get(key) || {
          ...status,
          key,
          count: 0,
        };
        previous.count += 1;
        statusMap.set(key, previous);
        taskStatus = previous;
        if (this.#isCompletedStatus(status)) completedTasks += 1;
      }

      if (peopleColumn) {
        const rawPeopleValue = this.#getTaskColumnValue(task, peopleColumn);
        let assigneeUids = Array.isArray(rawPeopleValue) ? rawPeopleValue : [];
        if (!Array.isArray(rawPeopleValue)) {
          try {
            assigneeUids = JSON.parse(rawPeopleValue || '[]');
          } catch (error) {
            assigneeUids = [];
          }
        }

        const taskAssignees =
          Array.isArray(assigneeUids) && assigneeUids.length
            ? assigneeUids.map(uid => this.#resolveMemberName(uid))
            : ['Unassigned'];

        taskAssignees.forEach(name => {
          const existing = assigneeMap.get(name) || {
            name,
            count: 0,
            statuses: {},
          };
          existing.count += 1;
          if (taskStatus) {
            existing.statuses[taskStatus.key] =
              (existing.statuses[taskStatus.key] || 0) + 1;
          }
          assigneeMap.set(name, existing);
        });
      }
    });

    const totalTasks = tasks.length;
    const statusDistribution = [...statusMap.values()].map(status => ({
      ...status,
      percent: Math.round((status.count / totalTasks) * 100),
      isCompleted: this.#isCompletedStatus(status),
    }));
    const assignees = [...assigneeMap.values()].sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    );

    return {
      totalTasks,
      completedTasks,
      completionPercent: Math.round((completedTasks / totalTasks) * 100),
      statusDistribution,
      statusOrder,
      assignees,
    };
  }

  #getTaskModelThumbnail(task) {
    if (task.thumbnail) return task.thumbnail;

    const reconstruction = (task.reconstructions || []).find(
      item => item?.thumbnail,
    );
    return reconstruction?.thumbnail || null;
  }

  #buildPdfAssets = async (exportOptions = {}, reportContext = null) => {
    const assets = {};
    const shouldBuildAssets =
      exportOptions.includeBlueprint || exportOptions.includeIsometric;
    const assetTools = shouldBuildAssets
      ? await import('@utils/aiReportPdfAssets.js')
      : null;
    const tasks = this.#getReportTasksForStats(reportContext);
    const taskAssets = shouldBuildAssets
      ? tasks
          .filter(task => task.reconstructions?.length || task.thumbnail)
          .map(task => ({
            taskId: task.id,
            taskName: task.name || 'Untitled task',
            wantsBlueprint: Boolean(exportOptions.includeBlueprint),
            wantsIsometric: Boolean(exportOptions.includeIsometric),
            isometricUrl: exportOptions.includeIsometric
              ? this.#getTaskModelThumbnail(task)
              : null,
          }))
      : [];
    console.log('Initial task assets for PDF:', taskAssets, exportOptions);
    if (exportOptions.includeBlueprint) {
      try {
        const blueprintAsset = await assetTools.createBlueprintSvgForPdf();
        console.log('Generated blueprint asset for PDF:', blueprintAsset);
        if (blueprintAsset) {
          const activeTaskId = CadFileState.cadfile?.cadFileID;
          const activeTask = tasks.find(
            task => String(task.id) === String(activeTaskId),
          );
          const targetAsset =
            taskAssets.find(
              asset => String(asset.taskId) === String(activeTaskId),
            ) ||
            (activeTask
              ? {
                  taskId: activeTask.id,
                  taskName: activeTask.name || 'Active task',
                  wantsBlueprint: true,
                  wantsIsometric: Boolean(exportOptions.includeIsometric),
                }
              : null);

          if (targetAsset) {
            targetAsset.blueprintSvg = blueprintAsset;
            if (!taskAssets.includes(targetAsset))
              taskAssets.unshift(targetAsset);
          } else {
            assets.blueprintSvg = blueprintAsset;
          }
        }
      } catch (error) {
        console.warn('Failed to generate blueprint for PDF export:', error);
        toast.warning(
          i18next.t('sidebar.aiAnalyzer.toast.blueprint_export_warning'),
        );
      }
    }

    if (exportOptions.includeIsometric) {
      try {
        console.log('Generating isometric asset for PDF export with options:', {
          includeBlueprint: exportOptions.includeBlueprint,
        });
        const isometricAsset = await assetTools.createIsometricPngForPdf();
        console.log('Generated isometric asset for PDF:', isometricAsset);
        if (isometricAsset) {
          const activeTaskId = CadFileState.cadfile?.cadFileID;
          const activeTask = tasks.find(
            task => String(task.id) === String(activeTaskId),
          );
          const targetAsset =
            taskAssets.find(
              asset => String(asset.taskId) === String(activeTaskId),
            ) ||
            (activeTask
              ? {
                  taskId: activeTask.id,
                  taskName: activeTask.name || 'Active task',
                  wantsBlueprint: Boolean(exportOptions.includeBlueprint),
                  wantsIsometric: true,
                }
              : null);

          if (targetAsset) {
            targetAsset.isometricPng = isometricAsset;
            if (!taskAssets.includes(targetAsset))
              taskAssets.unshift(targetAsset);
          } else {
            assets.isometricPng = isometricAsset;
          }
        }
      } catch (error) {
        console.warn(
          'Failed to generate isometric view for PDF export:',
          error,
        );
        toast.warning(
          i18next.t('sidebar.aiAnalyzer.toast.isometric_export_warning'),
        );
      }
    }

    if (taskAssets.length) assets.taskAssets = taskAssets;
    return assets;
  };

  #handleDownloadPdf = async (messageOrId, exportOptions = {}) => {
    try {
      const id =
        typeof messageOrId === 'string' ? messageOrId : messageOrId?.createdAt;
      const reportContext = await this.#enrichReportContextWithQuotations(
        messageOrId?.reportContext,
      );
      const pdfOptions = {
        ...this.#getPdfReportOptions(reportContext),
        reportStats: this.#buildPdfReportStats(reportContext),
        assets: await this.#buildPdfAssets(exportOptions, reportContext),
      };

      await handleDownloadPdf(`${id}-content`, pdfOptions);
      toast.success(i18next.t('sidebar.aiAnalyzer.toast.download_success'));
    } catch (error) {
      toast.error(i18next.t('sidebar.aiAnalyzer.toast.download_error'));
    }
  };

  #createViewContainers() {
    // Initialize view state
    this.currentView = 'chat'; // 'chat', 'history', 'bookmarks', 'upload'

    // Create containers for different views
    this.chatView = new UIDiv();
    this.chatView.addClass('flex flex-col h-full');
    if (IS_MOBILE() && this.isPage)
      this.chatView.addClass('!h-[calc(100%-50px)]');

    this.historyView = new UIDiv();
    this.historyView.addClass('flex flex-col h-full hidden');

    this.bookmarksView = new UIDiv();
    this.bookmarksView.addClass('flex flex-col h-full hidden');

    this.uploadView = new UIDiv();
    this.uploadView.addClass('flex flex-col h-full hidden');
  }

  #createViewHeader(title, onBack) {
    const header = new UIDiv();
    header.addClass('py-1.5 border-b border-gray-200 flex items-center gap-1');

    const titleText = new UIText(title);
    titleText.addClass('font-semibold text-[25px]');

    const backButton = new UIDiv();
    backButton.addClass(
      'cursor-pointer p-2 hover:bg-gray-100 rounded-md transition-colors',
    );
    const backIcon = new ArrowLeftIcon({ size: 20, color: 'none' });
    backButton.add(backIcon);
    backButton.onClick(onBack);

    header.add(backButton);
    header.add(titleText);

    return header;
  }

  #showChatView() {
    this.currentView = 'chat';
    this.chatView.removeClass('hidden');
    this.historyView.addClass('hidden');
    this.bookmarksView.addClass('hidden');
    this.uploadView.addClass('hidden');

    // Clear selection mode when returning to chat
    IsAiSelectionModeState.setIsAiSelectionMode(false);
    aiSelectionModeChanged.dispatch(false);
    AiSelectedContentState.setSelectedContent(new Map());
  }

  #updateHeaderMode(mode) {
    this.detectHeaderTitle?.setClass('hidden');
    if (!this.selectionModeToggleBtn) return;

    if (mode === 'detect') {
      if (IsAiSelectionModeState.isAiSelectionMode) {
        IsAiSelectionModeState.setIsAiSelectionMode(false);
        aiSelectionModeChanged.dispatch(false);
        AiSelectedContentState.setSelectedContent(new Map());
      }
      this.selectionModeToggleBtn.addClass('hidden');
      return;
    }

    this.selectionModeToggleBtn.removeClass('hidden');
    this.#updateSelectionButtonState();
  }

  #showHistoryView() {
    if (this.reportSection?.getMode?.() === 'detect') {
      this.#showSpatialVisionHistory();
      return;
    }

    this.currentView = 'history';
    this.chatView.addClass('hidden');
    this.historyView.removeClass('hidden');
    this.bookmarksView.addClass('hidden');
    // Load history when showing the view
    this.historyComponent.loadHistory();
  }

  async #showSpatialVisionHistory() {
    this.spatialVisionHistoryOpen = true;
    this.spatialVisionHistoryPanel?.update?.(
      SpatialVisionState.spatialVision,
      true,
    );
    try {
      if (SpatialVisionState.spatialVision.jobs?.length) {
        await SpatialVisionState.refreshJobs();
      } else {
        await SpatialVisionState.load();
      }
    } catch (error) {
      console.error('Failed to load Spatial Vision history:', error);
      toast.error('Failed to load Spatial Vision history.');
    }
  }

  #closeSpatialVisionHistory() {
    this.spatialVisionHistoryOpen = false;
    this.spatialVisionHistoryPanel?.update?.(
      SpatialVisionState.spatialVision,
      false,
    );
  }

  #handleSpatialVisionHistorySelect(job) {
    if (!job || !this.currentSessionId) return;

    const currentSession = this.chatSessions.get(this.currentSessionId);
    const lastAssistant = [...(currentSession?.chatHistory || [])]
      .reverse()
      .find(message => message.role === 'assistant');
    if (String(lastAssistant?.spatialVisionJob?.id) === String(job.id)) {
      this.#showChatView();
      return;
    }

    const title =
      job.prompt ||
      (job.type === 'sequence' ? 'Sequence run' : 'Vision chat run');
    this.#addMessageToHistory({
      role: 'user',
      content: title,
      createdAt: new Date().toISOString(),
      sessionId: this.currentSessionId,
      spatialVisionInput: {
        imageName:
          job.imageResults?.length > 1
            ? `${job.imageResults.length} images`
            : job.imageResults?.[0]?.name,
        sequenceName: job.type === 'sequence' ? job.sequenceName : null,
      },
      hideActions: true,
    });
    this.#addMessageToHistory({
      role: 'assistant',
      content: 'Spatial Vision result',
      createdAt: new Date().toISOString(),
      sessionId: this.currentSessionId,
      spatialVisionJob: job,
      hideActions: true,
    });
    this.#toggleWelcomeScreen(true);
    this.#showChatView();
  }

  #showBookmarksView() {
    this.currentView = 'bookmarks';
    this.chatView.addClass('hidden');
    this.historyView.addClass('hidden');
    this.bookmarksView.removeClass('hidden');
    // Load bookmarks when showing the view
    this.bookmarksComponent.loadBookmarks();
  }

  // Build the entire component by adding header and content.
  #buildComponent() {
    // if User is not logged in, do not render the component
    if (UserState.user === null) return;
    // Main content container
    const contentContainer = new UIDiv();
    contentContainer.setId('ai-analyzer-content-container');

    contentContainer.addClass(
      `relative h-full flex flex-col pt-0.5 overflow-hidden ${!IS_MOBILE() && 'pl-2'}`,
    );

    // Initialize view containers
    this.#createViewContainers();

    // CHAT VIEW CONTENT
    // Create a header container for chat tabs and credits
    this.normalHeader = new UIDiv();
    this.normalHeader.addClass(
      'flex items-center justify-between mb-2 border-b border-gray-200',
    );
    const chatHeader = this.normalHeader;

    // Change 'space' only in llm page
    if (this.isPage) {
      if (IS_MOBILE()) {
        const urlParams = new CaseInsensitiveURLSearchParams(
          window.location.search,
        );
        const from = urlParams.get('from') || 'Chat'; // e.g., 'chat' or 'projects'
        const roomId = urlParams.get('roomId'); // the chat room id
        const pId = urlParams.get('pId'); // the project id
        // Construct return URL based on 'from' parameter
        let returnUrl = '';
        if (from === 'chat' && roomId) {
          returnUrl += `/chat?roomId=${roomId}`;
        } else if (from === 'projects' && pId) {
          returnUrl += `/projects?pId=${pId}`;
        } else if (from === 'projects') {
          returnUrl += '/projects';
        } else if (from === 'chat') {
          returnUrl += '/chat';
        } else {
          returnUrl += '/';
        }

        const returnButton = new UIDiv();
        returnButton.setClass(
          'flex items-center gap-2 pt-1 pb-2 border-b w-full border-gray-200',
        );

        const button = new UIButton();
        const backIcon = new ChevronIcon({ color: 'none', size: 24 });
        backIcon.setClass('rotate-180');
        button.onClick(() => {
          route(returnUrl);
        });
        button.add(backIcon);

        const title = new UIParagraph();
        title.setClass('text-[30px] font-bold truncate');
        title.setTextContent(
          'Return to ' + from.charAt(0).toUpperCase() + from.slice(1),
        );

        returnButton.add(button);
        returnButton.add(title);

        contentContainer.add(returnButton);
      }

      const projectTypePicker = new ProjectTypePicker({
        projectType: UserState.user?.selectedProjectsType || 'personal',
        handleProjectTypeChange: async type => {
          // we need to check if the user is allowed to change to team project - if there is no org then it cant be possible
          if (type === 'team' && OrganizationState.organizations.length === 0) {
            toast.warning(i18next.t('toast.changeTypeTeamError'));
            return 'error';
          }

          // Handle user details update if needed
          if (type === UserState.user?.selectedProjectsType) return;
          try {
            await editUserDetails({ selectedProjectsType: type });
            UserState.setUser({
              ...UserState.user,
              selectedProjectsType: type,
            });
          } catch (error) {
            console.error('Error updating user details:', error);
          }
        },
        minifiedView: true,
      });
      chatHeader.add(projectTypePicker);
    }

    // Chat tabs for multiple sessions (takes remaining width)
    this.chatTabs = new AiAnalyzerChatTabs(
      () => {
        const sessionId = this.#createNewSession();
        this.chatTabs.addTab(sessionId);
        this.#switchSession(sessionId);
      },
      sessionId => this.#switchSession(sessionId),
      sessionId => this.#closeSession(sessionId),
    );
    this.chatTabs.addClass('flex-1 mr-3'); // Add flex-1 to take remaining width and margin-right for spacing
    chatHeader.add(this.chatTabs);

    // Selection mode toggle button (for desktop)
    if (!IS_MOBILE()) {
      this.selectionModeToggleBtn = new UIButton();
      this.selectionModeToggleBtn.addClass(
        'px-3 py-1.5 rounded-full text-[17.5px] flex items-center gap-2 transition-all flex-shrink-0 mr-2 bg-slate-100 hover:bg-slate-200 data-[active=true]:bg-blue-100 data-[active=true]:hover:bg-blue-200 data-[active=true]:ring-2 data-[active=true]:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed',
      );
      const selectionIcon = new UIIcon('mouse-pointer', {
        color: 'none',
        size: 16,
        strokeColor: '#475569',
      });
      this.selectionModeToggleBtn.add(selectionIcon);
      const selectionText = new UIText('Select');
      selectionText.addClass('text-slate-600 font-medium');
      this.selectionModeToggleBtn.add(selectionText);
      this.selectionModeToggleBtn.onMouseEnter(() => {
        Tooltip.show(
          this.selectionModeToggleBtn,
          'Select content (Ctrl/Cmd + E)',
          'bottom',
        );
      });
      this.selectionModeToggleBtn.onMouseLeave(() => {
        Tooltip.hide();
      });
      this.selectionModeToggleBtn.onClick(() => {
        this.#toggleSelectionMode();
      });
      // Initially disable if no content
      this.#updateSelectionButtonState();
      chatHeader.add(this.selectionModeToggleBtn);
    }

    // Credits indicator (on the right side)
    const creditButton = new UIDiv();
    creditButton.addClass(
      'px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-full text-[17.5px] text-slate-600 flex items-center gap-2 transition-colors cursor-pointer flex-shrink-0',
    );
    const creditIcon = new CoinsIcon({ color: 'none', size: 16 });
    creditButton.add(creditIcon);
    creditButton.add(this.creditsCount);
    creditButton.onClick(e => {
      e.stopPropagation();

      if (window.webkit?.messageHandlers?.creditsTap) {
        window.webkit.messageHandlers.creditsTap.postMessage('clicked');
      } else if (window.AndroidInterface?.creditsTap) {
        window.AndroidInterface.creditsTap('clicked');
      } else {
        route('/settings?tab=pricing');
      }
    });
    chatHeader.add(creditButton);

    this.chatView.add(chatHeader);

    // Scrollable content area for analysis results
    this.messagesContainer = new UIDiv();
    this.messagesContainer.addClass(
      'flex-1 overflow-y-auto mb-4 relative h-full',
    );
    this.analysisResult = new AiAnalyzerResult(
      (message, exportOptions) =>
        this.#handleDownloadPdf(message, exportOptions),
      () => this.bookmarksComponent.refreshBookmarks(),
      pendingRequest => this.#handleRetryRequest(pendingRequest),
      this.currentSessionId,
    );

    // Add long-press handler for selection mode activation
    this.#addLongPressHandler();
    this.messagesContainer.add(this.analysisResult);

    // Add confirmation component
    this.confirmationComponent = new AiAnalyzerConfirmation(
      token => this.#handleConfirmAction(token),
      () => this.#handleRejectAction(),
    );
    this.messagesContainer.add(this.confirmationComponent);

    this.chatView.add(this.messagesContainer);

    // Set initial visibility - welcome screen
    this.welcomeScreen = new AiAnalyzerEmptyState(
      tool => {
        this.reportSection.setPrompt(tool);
        setTimeout(async () => await this.#onCreateReport(), 0);
      },
      window.location.pathname.includes('viewer'),
      mode => {
        // Sync mode change from welcome screen toggle to report section badge
        this.reportSection.setMode(mode);
      },
      {
        getSelection: () => this.reportSection?.getDetectSelection?.() || {},
        onSelect: image => {
          const selection = this.reportSection?.getDetectSelection?.() || {};
          const currentImages = selection.images || [];
          const exists = currentImages.some(
            item =>
              item.path === image.path &&
              String(item.taskId) === String(image.taskId),
          );
          if (!exists && isVlmImageOversized(image)) {
            this.reportSection.showDetectImageValidation(
              [image],
              currentImages.length,
            );
            return;
          }
          this.reportSection.clearDetectImageValidation();
          const isCloudDirect =
            SpatialVisionState.spatialVision.selectedServiceTier === 'cloud' &&
            selection.runMode !== 'sequence';
          if (isCloudDirect) {
            const images = exists
              ? currentImages.filter(
                  item =>
                    !(
                      item.path === image.path &&
                      String(item.taskId) === String(image.taskId)
                    ),
                )
              : [...currentImages, image].slice(0, 10);
            this.reportSection?.setDetectSelection?.({
              ...selection,
              image: images[0] || null,
              images,
            });
            return;
          }
          this.reportSection?.setDetectSelection?.({
            ...selection,
            image,
            images: [image],
          });
        },
      },
    );
    this.chatView.add(this.welcomeScreen);

    // Fixed bottom section with textarea
    const bottomSection = new UIDiv();
    bottomSection.addClass('flex-shrink-0 px-1');

    this.reportSection = new AiAnalyzerReportSection(
      () => this.#onCreateReport(),
      () => this.#showHistoryView(),
      () => this.#showBookmarksView(),
      (command, response) => {
        // Display command and response as chat messages
        this.#addMessageToHistory({
          role: 'user',
          content: command,
        });
        this.#addMessageToHistory({
          role: 'assistant',
          content: response,
        });
      },
      mode => {
        // Handle mode change - update welcome screen
        this.welcomeScreen.setMode(mode);
        this.#updateHeaderMode(mode);
        if (mode !== 'detect') this.#closeSpatialVisionHistory();
      },
      hide => {
        if (!hide && this.welcomeScreen) {
          this.#toggleWelcomeScreen(true);
        }
      },
      selection => {
        const session = this.chatSessions.get(this.currentSessionId);
        if (session) session.detectSelection = selection;
        this.welcomeScreen?.refreshDetectSelection?.();
      },
    );
    bottomSection.add(this.reportSection);
    this.chatView.add(bottomSection);
    this.#updateHeaderMode(this.reportSection.getMode());

    // HISTORY VIEW CONTENT
    const historyHeader = this.#createViewHeader(
      i18next.t('sidebar.aiAnalyzer.tabs.history'),
      () => this.#showChatView(),
    );
    this.historyView.add(historyHeader);

    const historyContent = new UIDiv();
    historyContent.addClass('flex-1 overflow-hidden');
    this.historyView.add(historyContent);

    // BOOKMARKS VIEW CONTENT
    const bookmarksHeader = this.#createViewHeader(
      i18next.t('sidebar.aiAnalyzer.tabs.bookmarks'),
      () => this.#showChatView(),
    );
    this.bookmarksView.add(bookmarksHeader);

    const bookmarksContent = new UIDiv();
    bookmarksContent.addClass('flex-1 overflow-hidden');
    this.bookmarksView.add(bookmarksContent);

    // Add all views to content container
    contentContainer.add(this.chatView);
    contentContainer.add(this.historyView);
    contentContainer.add(this.bookmarksView);
    contentContainer.add(this.uploadView);

    this.spatialVisionHistoryPanel = new VisionHistoryPanel(
      () => this.#closeSpatialVisionHistory(),
      job => this.#handleSpatialVisionHistorySelect(job),
    );
    contentContainer.dom.appendChild(this.spatialVisionHistoryPanel.dom);

    // Add main content to the component
    this.add(contentContainer);

    // Initialize components
    this.historyComponent = new AiAnalyzerHistory(thread =>
      this.loadThread(thread),
    );
    historyContent.add(this.historyComponent);

    this.bookmarksComponent = new AiAnalyzerBookmarks(message =>
      this.copyBookmark(message),
    );
    bookmarksContent.add(this.bookmarksComponent);

    // Create initial chat session
    const initialSessionId = this.#createNewSession();
    this.chatTabs.addTab(initialSessionId);

    // Before switching to the new session, check if FoldersState already has folders
    // (e.g., set by #handleOpenAiAnalyser before navigation on mobile)
    const existingFolders = FoldersState.folders;
    if (existingFolders && existingFolders.length > 0) {
      // Save existing folders to the new session before switching
      const session = this.chatSessions.get(initialSessionId);
      session.selectedFiles = this.getSelectedFilesFromFoldersState();
    }

    // Check if AiSelectedItems already has selections (e.g., set from project list bottom bar)
    const existingAiSelection = AiSelectedItems.selectedItems;
    if (
      existingAiSelection.projects.length > 0 ||
      existingAiSelection.tasks.length > 0
    ) {
      const session = this.chatSessions.get(initialSessionId);
      session.selectedProjectIds = existingAiSelection.projects;
      session.selectedTaskIds = existingAiSelection.tasks;
    } else if (this.isPage) {
      // Restore from URL params on mobile (fallback if state was lost)
      const urlParams = new CaseInsensitiveURLSearchParams(
        window.location.search,
      );
      const aiProjects = urlParams.get('aiProjects');
      const aiTasks = urlParams.get('aiTasks');
      if (aiProjects || aiTasks) {
        const projectIds = aiProjects
          ? aiProjects.split(',').map(Number).filter(Boolean)
          : [];
        const taskIds = aiTasks
          ? aiTasks.split(',').map(Number).filter(Boolean)
          : [];
        AiSelectedItems.setSelectedItems({
          projects: projectIds,
          tasks: taskIds,
        });
        const session = this.chatSessions.get(initialSessionId);
        session.selectedProjectIds = projectIds;
        session.selectedTaskIds = taskIds;
      }
    }

    this.#switchSession(initialSessionId);

    // Update selection button state after initial setup
    this.#updateSelectionButtonState();
  }

  #addLongPressHandler() {
    let longPressTimer;
    const longPressDuration = 500; // ms

    this.messagesContainer.onTouchStart(e => {
      const selectableElement = e.target.closest('.selectable-element');
      if (!selectableElement || this.isSelectionMode) return;

      longPressTimer = setTimeout(() => {
        // Activate selection mode
        IsAiSelectionModeState.setIsAiSelectionMode(true);
        aiSelectionModeChanged.dispatch(true);

        // Select the long-pressed element
        const elementId = selectableElement.dataset.selectableId;
        if (elementId) {
          const selectedContent = new Map();
          selectedContent.set(elementId, {
            sessionId: this.currentSessionId,
            content: selectableElement.textContent.trim(),
            htmlContent: selectableElement.innerHTML,
          });
          AiSelectedContentState.setSelectedContent(selectedContent);
        }
      }, longPressDuration);
    });

    this.messagesContainer.onTouchEnd(() => {
      clearTimeout(longPressTimer);
    });

    this.messagesContainer.onTouchMove(() => {
      clearTimeout(longPressTimer);
    });
  }

  #createSelectionHeader() {
    const header = new UIDiv();
    header.addClass(
      'flex items-center gap-2.5 pb-2 px-2 border-b border-gray-200',
    );

    const leftSection = new UIDiv().addClass('flex items-center gap-2.5');

    // Close button
    const closeButton = new UIButton();
    closeButton.setClass(
      'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[17.5px] font-medium transition-all shrink-0 outline-none hover:bg-gray-100 h-10 w-10',
    );
    const closeIcon = new UIIcon('X', {
      color: 'none',
      size: 18,
      strokeColor: '#0c121a',
    });
    closeButton.onClick(() => {
      IsAiSelectionModeState.setIsAiSelectionMode(false);
      aiSelectionModeChanged.dispatch(false);
      AiSelectedContentState.setSelectedContent(new Map());
    });
    closeButton.add(closeIcon);
    leftSection.add(closeButton);

    // Selection count
    this.selectionCountText = new UIParagraph();
    this.selectionCountText.setClass('text-[30px] font-bold truncate');
    this.selectionCountText.setTextContent('0');
    leftSection.add(this.selectionCountText);

    header.add(leftSection);

    // Actions container (right side)
    const actionsContainer = new UIDiv().setClass(
      'ml-auto flex items-center gap-2',
    );

    // Export button
    const exportButton = new UIButton();
    exportButton.setClass(
      'inline-flex items-center justify-center whitespace-nowrap text-[17.5px] transition-all shrink-0 outline-none bg-[var(--o-hive-primary-80)] hover:bg-[var(--o-hive-primary-100)] text-white h-8 rounded-md px-2.5 gap-1.5 font-medium',
    );
    const exportIcon = new UIIcon('upload', {
      color: 'none',
      size: 16,
      strokeColor: 'white',
    });
    exportButton.add(exportIcon);
    const exportText = new UIParagraph().setClass('text-[17.5px]');
    exportText.setTextContent('Export');
    exportButton.add(exportText);
    exportButton.onClick(() => {
      Popover.show({
        trigger: exportButton,
        content: this.#createExportPopoverContent(),
        hideOnSelect: true,
      });
    });
    actionsContainer.add(exportButton);

    header.add(actionsContainer);

    return header;
  }

  #createExportPopoverContent() {
    const container = new UIDiv().setClass('p-2 w-72');

    // Header
    const header = new UIDiv().setClass(
      'flex items-center justify-between px-2 pb-1 border-b border-gray-100',
    );
    const headerTitle = new UIDiv().setClass('flex flex-col');
    const title = new UIParagraph().setClass('text-[20px] font-semibold');
    title.setTextContent('Export Content');
    const subtitle = new UIParagraph().setClass('text-[15px] text-gray-500');
    const count = AiSelectedContentState.selectedContent.size;
    subtitle.setTextContent(`${count} item${count !== 1 ? 's' : ''} selected`);
    headerTitle.add(title, subtitle);
    header.add(headerTitle);
    container.add(header);

    // Action items
    const actionItems = [
      {
        key: 'create-task',
        icon: 'list-checks',
        iconColor: '#10B981',
        title: 'Create Task',
        description: 'Turn selected content into new tasks',
      },
      {
        key: 'update-task',
        icon: 'list-plus',
        iconColor: '#3B82F6',
        title: 'Add to Task',
        description: 'Update existing tasks with content',
      },
      {
        key: 'create-project',
        icon: 'folder-plus',
        iconColor: '#8B5CF6',
        title: 'Create Project',
        description: 'Start a new project from content',
      },
      {
        key: 'update-project',
        icon: 'folder-sync',
        iconColor: '#F59E0B',
        title: 'Add to Project',
        description: 'Attach content to existing project',
      },
    ];

    const actionsList = new UIDiv().setClass('flex flex-col py-1');

    actionItems.forEach(item => {
      const actionButton = new UIButton();
      actionButton.setClass(
        'flex items-center gap-3 px-2 py-2.5 hover:bg-gray-50 rounded-lg transition-colors w-full text-left',
      );

      const iconContainer = new UIDiv().setClass(
        'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
      );
      iconContainer.setStyle('backgroundColor', [`${item.iconColor}15`]);
      const icon = new UIIcon(item.icon, {
        color: 'none',
        size: 18,
        strokeColor: item.iconColor,
      });
      iconContainer.add(icon);

      const textContent = new UIDiv().setClass('flex-1 min-w-0');
      const actionTitle = new UIParagraph().setClass(
        'text-[17.5px] font-medium text-gray-900',
      );
      actionTitle.setTextContent(item.title);
      const actionDesc = new UIParagraph().setClass(
        'text-[15px] text-gray-500 truncate',
      );
      actionDesc.setTextContent(item.description);
      textContent.add(actionTitle, actionDesc);

      const arrowIcon = new UIIcon('chevron', {
        color: 'none',
        size: 16,
        strokeColor: '#9CA3AF',
      });

      actionButton.add(iconContainer, textContent, arrowIcon);

      actionButton.onClick(() => {
        Popover.hide();
        aiSelectionActionChanged.dispatch(item.key);
      });

      actionsList.add(actionButton);
    });

    container.add(actionsList);

    return container;
  }

  #toggleSelectionMode() {
    if (this.reportSection?.getMode?.() === 'detect') return;
    const newState = !IsAiSelectionModeState.isAiSelectionMode;
    IsAiSelectionModeState.setIsAiSelectionMode(newState);
    aiSelectionModeChanged.dispatch(newState);
    if (!newState) {
      AiSelectedContentState.setSelectedContent(new Map());
    }
  }

  #toggleSelectionModeUI(isActive) {
    if (isActive) {
      // Update selection toggle button styling using data attribute
      if (this.selectionModeToggleBtn) {
        this.selectionModeToggleBtn.dom.setAttribute('data-active', 'true');
      }

      // Create and show selection header
      if (!this.selectionHeader) {
        this.selectionHeader = this.#createSelectionHeader();
      }

      // Replace normal header with selection header
      if (this.normalHeader && this.normalHeader.dom.parentNode) {
        this.normalHeader.dom.parentNode.insertBefore(
          this.selectionHeader.dom,
          this.normalHeader.dom,
        );
        this.normalHeader.addClass('hidden');
      }

      // Hide report section (input area)
      if (this.reportSection) {
        this.reportSection.addClass('hidden');
      }
    } else {
      // Reset selection toggle button styling
      if (this.selectionModeToggleBtn) {
        this.selectionModeToggleBtn.dom.setAttribute('data-active', 'false');
      }

      // Show normal header
      if (this.normalHeader) {
        this.normalHeader.removeClass('hidden');
      }

      // Remove selection header
      if (this.selectionHeader && this.selectionHeader.dom.parentNode) {
        this.selectionHeader.dom.remove();
      }

      // Show report section
      if (this.reportSection) {
        this.reportSection.removeClass('hidden');
      }

      // Clear selections
      AiSelectedContentState.setSelectedContent(new Map());
    }
  }

  #updateSelectionCount() {
    if (this.selectionCountText) {
      const count = AiSelectedContentState.selectedContent.size;
      this.selectionCountText.setTextContent(`${count}`);
    }
  }

  #updateSelectionButtonState() {
    if (!this.selectionModeToggleBtn) return;

    // Check if current session has any selectable content
    const currentSession = this.chatSessions.get(this.currentSessionId);
    const hasSelectableContent =
      currentSession && currentSession.chatHistory.length > 0;

    this.selectionModeToggleBtn.dom.disabled = !hasSelectableContent;
  }

  #handleSelectionAction(action) {
    // Navigate to upload view with selected content
    aiViewChange.dispatch('upload-ai-content', action);
  }

  #handleAiViewChange(view, data) {
    if (view === 'upload-ai-content') {
      this.#showUploadView(data);
    } else if (view === 'back-to-chat') {
      this.#showChatView();
    }
  }

  #showUploadView(action) {
    // Get selected content from state
    const selectedContent = AiSelectedContentState.selectedContent;

    if (selectedContent.size === 0) {
      toast.error('No content selected');
      return;
    }

    // Hide other views
    this.currentView = 'upload';
    this.chatView.addClass('hidden');
    this.historyView.addClass('hidden');
    this.bookmarksView.addClass('hidden');
    this.uploadView.removeClass('hidden');

    // Clear and create upload view
    this.uploadView.clear();
    const uploadViewComponent = new UploadMessagesView(
      action,
      selectedContent,
      () => {
        // Return to chat callback
        aiViewChange.dispatch('back-to-chat');
      },
      [], // roomUsers (empty for AI mode)
      this.currentSessionId, // sessionId
      'ai', // mode
    );
    this.uploadView.add(uploadViewComponent);
  }
}

export { DrawerAiAnalyser };
